import React, { createContext, useContext, useState, useCallback } from "react";

/**
 * Session state (user, company, preferences) as real React state.
 * Replaces the mutable `globals` useRef that Cars.jsx used to spread into
 * GlobalContext once per login — mutations to that ref never re-rendered
 * consumers. Updates through these setters propagate immediately.
 *
 * GlobalContext still carries the same keys (spread from `session`), so
 * consumers keep working unchanged; new code can use useAuth() directly.
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [session, setSession] = useState({
        //Pre-login placeholder only. The real value arrives with the
        ///login | /getuserdata payload as VatMultiplier and is written by
        //setSessionData below; nothing reads this before then, because the only
        //consumers are the vehicle modals' cost previews.
        //Named "rate" for historical reasons — it is the multiplier (1 + rate).
        vatRate: 1.18,
        DealershipID: undefined,
        UserID: undefined,
        EmailAddress: undefined,
        userData: null,
        companyData: null,
        fieldOrder: {},
        // Fetched once after login via GET /ai/status; defaults to false so
        // AiButton stays hidden until the probe succeeds.
        aiEnabled: false,
    });

    //Maps the /getuserdata | /login payload into session state
    //(formerly setUserData in Cars.jsx)
    const setSessionData = useCallback((data) => {
        setSession(prev => ({
            ...prev,
            DealershipID: data.DealershipID,
            UserID: data.UserID,
            EmailAddress: data.EmailAddress,
            //Server-owned: 1 + VAT_RATE from the API's .env, the same constant
            //its cost SQL multiplies demurrage by. Falling back to the initial
            //value only matters against an API too old to send it, which is a
            //deploy-ordering window, not a second source of truth.
            vatRate: data.VatMultiplier ?? prev.vatRate,
            userData: {
                UserID: data.UserID,
                EmailAddress: data.EmailAddress,
                FirstName: data.FirstName,
                LastName: data.LastName,
                Phone1: data.Phone1,
                Phone2: data.Phone2,
                Permissions: data.Permissions,
                //Permission strings + role names resolved live at login
                //(server Phase 1.5). can() prefers PermList; Permissions stays
                //for the legacy fallback until Phase 7.
                PermList: data.PermList,
                RoleList: data.RoleList,
                //Phase 8: cost fields this dealership gates; the client hides
                //them when the user lacks inventory.costs (server still redacts).
                CostGatedFields: data.CostGatedFields,
                FirstLogin: data.FirstLogin
            },
            fieldOrder: data.Preferences,
            companyData: {
                CompanyName: data.CompanyName,
                ContactPhone1: data.ContactPhone1,
                ContactPhone2: data.ContactPhone2,
                BondName: data.BondName,
                EmailAddress: data.CompanyEmail,
                LogoURL: data.LogoURL,
                DailyPenalty: data.DailyPenalty,
                //Suggested monthly tracker rental for new credit deals. NULL for
                //any tenant that has not set one — the sell form then simply
                //offers no suggestion rather than seeding a made-up figure.
                DefaultTrackerFee: data.DefaultTrackerFee,
                //Which kinds of storage this dealership uses — a bonded
                //warehouse, its own yard, or both — and what they are called.
                //Decides whether a vehicle is asked for a demurrage rate at all,
                //and seeds the Location on a new one. NULL until somebody answers
                //it in Settings, which validation/storage.js reads as bond-only:
                //the behaviour every tenant had before the setting existed.
                StorageSettings: data.StorageSettings,
                validity: data.Validity,
                DefaultCreditPeriod: data.DefaultCreditPeriod,
                DefaultDownPayment: data.DefaultDownPayment,
                FoundingYear: data.FoundingYear,
                Mission: data.Mission,
                History: data.History,
                CustomerSatisfaction: data.CustomerSatisfaction,
                TeamSize: data.TeamSize,
                //Tenant theme preset, e.g. { preset: "teal" } (see theme.js)
                ThemeSettings: data.ThemeSettings,
                //Origin of this tenant's public cartalog site, or null. Gates
                //the "link the vehicle" option in the broadcast composer.
                PublicSiteUrl: data.PublicSiteUrl
            }
        }));
    }, []);

    const updateCompany = useCallback((partial) => {
        setSession(prev => ({ ...prev, companyData: { ...prev.companyData, ...partial } }));
    }, []);

    const updateUser = useCallback((partial) => {
        setSession(prev => ({ ...prev, userData: { ...prev.userData, ...partial } }));
    }, []);

    const updateFieldOrder = useCallback((sheetName, order) => {
        setSession(prev => ({ ...prev, fieldOrder: { ...prev.fieldOrder, [sheetName]: order } }));
    }, []);

    // Called once per login after GET /ai/status resolves.
    const setAiEnabled = useCallback((enabled) => {
        setSession(prev => ({ ...prev, aiEnabled: Boolean(enabled) }));
    }, []);

    return (
        <AuthContext.Provider value={{ session, setSessionData, updateCompany, updateUser, updateFieldOrder, setAiEnabled }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
}
